import React, { useState } from 'react';
import ZentorgaForm from '../components/ZentorgaForm';
import SurveyQuestion from '../components/SurveyQuestion';
import BenefitPeriod from '../../components/BenefitPeriod';
import KeepButton from '../components/KeepButton';
import SoundVideo from '../components/SoundVideo';
import { useFormSubmit } from '../hooks/useFormSubmit';
import { useMultiStep } from '../hooks/useMultiStep';
import { getImagesByStep } from '../utils/templateMapper';
import '../styles/zentorga.scss';
import '../styles/template26.scss';

/**
 * Template26 - 두 단계 템플릿 (1개 설문 + 폼 슬라이더 포함)
 * Template22 기반 + 495 폼 슬라이더(form-image <-> form-section) 추가
 * Step 1: 이미지(imgUrl1~2) + Keep 버튼
 * Step 2: 이미지(imgUrl3~4) + 설문 1개 + 폼 슬라이더(imgUrl5: 폼이미지, imgUrl6: 혜택버튼) + 폼 + 푸터
 */
const Template26 = ({
  landingData,
  urlCode,
  urlCodeSettingId
}) => {
  const { handleSubmit, isSubmitting } = useFormSubmit(
    urlCode,
    urlCodeSettingId,
    landingData.endpoint
  );

  const { currentStep, goToNextStep } = useMultiStep(2);
  const [surveyAnswer, setSurveyAnswer] = useState('');
  const [formSliderActive, setFormSliderActive] = useState(false);

  const stepImages = getImagesByStep(landingData, 2);

  const handleKeepButton = () => {
    goToNextStep({
      selectedOption: surveyAnswer
    });
  };

  const handleFormSubmit = async (formData) => {
    const submitData = {
      ...formData,
      selectedOption: surveyAnswer
    };
    await handleSubmit(submitData);
  };

  const handleCostBtnClick = () => {
    setFormSliderActive(true);
  };

  return (
    <section className="template26-layout zentorga-layout">
      {/* Step 1 */}
      {currentStep === 0 && (
        <div className="step1 step-container">
          {stepImages.step1.length > 0 && (
            <div className="template26-images zentorga-images">
              {stepImages.step1.map((img, idx) => {
                const fileExtension = img.url.split('.').pop().toLowerCase();
                const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension);
                return isVideo ? (
                  <SoundVideo key={idx} src={img.url} loop={img.alt !== "false"} />
                ) : (
                  <img key={idx} src={img.url} alt={img.alt} />
                );
              })}
            </div>
          )}

          <KeepButton
            onClick={handleKeepButton}
            imageUrl={landingData.keepBtnUrl}
            text="다음 단계"
          />
        </div>
      )}

      {/* Step 2 */}
      {currentStep === 1 && (
        <div className="step2 step-container">
          {stepImages.step2.length > 0 && (
            <div className="template26-images zentorga-images">
              {stepImages.step2.map((img, idx) => {
                const fileExtension = img.url.split('.').pop().toLowerCase();
                const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension);
                return isVideo ? (
                  <SoundVideo key={idx} src={img.url} loop={img.alt !== "false"} />
                ) : (
                  <img key={idx} src={img.url} alt={img.alt} />
                );
              })}
            </div>
          )}

          {/* 폼 슬라이더 (495 스타일) */}
          <div className={`form-slider ${formSliderActive ? 'active' : ''}`}>
            {/* 슬라이더 왼쪽: 폼 이미지 + 혜택 버튼 */}
            <div className="form-image">
              {landingData.imgUrl5 && (
                <img src={landingData.imgUrl5} alt={landingData.imgALT5 || '폼 이미지'} />
              )}
              {landingData.imgUrl6 && (
                <div className="cost-btn" onClick={handleCostBtnClick}>
                  <img src={landingData.imgUrl6} alt={landingData.imgALT6 || '혜택 신청하기'} />
                </div>
              )}
            </div>

            {/* 슬라이더 오른쪽: 설문 + 폼 + 동의 + 버튼 */}
            <div className="form-section">
              <div className="surveySection">
                <SurveyQuestion
                  topic={landingData.surveyTitle1 || '설문'}
                  answers={[
                    landingData.surveyOpt1_1,
                    landingData.surveyOpt1_2,
                    landingData.surveyOpt1_3,
                    landingData.surveyOpt1_4,
                    landingData.surveyOpt1_5
                  ].filter(Boolean)}
                  onChange={setSurveyAnswer}
                  questionNumber={1}
                  displayType={landingData.surveyTopic?.topic?.[0]?.type || 'radio'}
                  numberColor={landingData.surveyTopic?.numberColor}
                  answerColor={landingData.surveyAnswer?.answerColor}
                />
              </div>

              {landingData.eventTarget && (
                <div className="eventTarget">
                  <p>이벤트 대상자 : <span className="event-target-text">{landingData.eventTarget}</span></p>
                </div>
              )}

              {landingData.benefitDate && (
                <div className="benefitDate">
                  <p>혜택 기간 : <BenefitPeriod periodType={landingData.benefitDate} /></p>
                </div>
              )}

              <div className="formStyleClassName">
                <ZentorgaForm
                  onSubmit={handleFormSubmit}
                  isSubmitting={isSubmitting}
                  formStyle={landingData.formStyleClassName || 'gray'}
                  consentStyle={landingData.privacyPolicyClassName || 'dark'}
                  privacyPolicyName={landingData.privacyPolicyName || '본원'}
                  privacyPolicy={landingData.privacyPolicy}
                  marketingPolicy={landingData.marketingPolicy}
                  buttonComponent={
                    <button
                      type="submit"
                      form="zentorga-form"
                      id="postBtn"
                      className={`postBtn ${landingData.postBtnLocate === 'fixed' ? 'postBtn-fixed' : ''}`}
                      disabled={isSubmitting}
                    >
                      {landingData.postBtnUrl ? (
                        <img src={landingData.postBtnUrl} alt="신청하기" />
                      ) : (
                        <span>{isSubmitting ? '신청 중...' : '신청하기'}</span>
                      )}
                    </button>
                  }
                />
              </div>

              {landingData.serviceInfoClassName && (
                <p
                  className={`serviceInfoClassName ${landingData.serviceInfoClassName}`}
                  id="serviceInfoClassName"
                >
                  * 본 서비스는 전국 20여 개 이상의 제휴 병원에서 제공되며,<br />
                  상담 신청 정보를 기반으로 전문 상담원이 적합한 제휴 병원을 안내해드립니다.
                </p>
              )}
            </div>
          </div>

          {landingData.footerClassName !== null && (landingData.footerClassName !== undefined || landingData.footerDetail) && (
            <footer className={`footerClassName ${landingData.footerClassName || ''}`}>
              {landingData.footerDetail && (
                <p className="footerDetail" style={{ whiteSpace: 'pre-line' }}>
                  {landingData.footerDetail}
                </p>
              )}
            </footer>
          )}
        </div>
      )}
    </section>
  );
};

export default Template26;
